import React, { useState, useRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import PhoneInputBase, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const StyledWrapper = styled.div`
  width: 100%;
  margin: 8px 0;

  .PhoneInput {
    display: flex;
    align-items: center;
    border: 1px solid #c4c4c4;
    border-radius: 4px;
    padding: 8.5px 14px;
    background: #fff;
    box-sizing: border-box;
    transition: border-color 0.15s ease;
  }

  .PhoneInput:focus-within {
    border-color: #0069ff;
  }

  &.has-error .PhoneInput {
    border-color: #d32f2f;
  }

  .PhoneInputCountry {
    margin-right: 8px;
  }

  .PhoneInputCountrySelect.PhoneInputCountrySelect.PhoneInputCountrySelect {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    background: transparent !important;
    padding: 0 !important;
  }

  .PhoneInputInput.PhoneInputInput.PhoneInputInput {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    background: transparent !important;
    font-size: inherit;
    font-family: inherit;
    flex: 1;
    min-width: 0;
  }

  .PhoneInputCountrySelect.PhoneInputCountrySelect.PhoneInputCountrySelect:focus,
  .PhoneInputInput.PhoneInputInput.PhoneInputInput:focus,
  .PhoneInputInput.PhoneInputInput.PhoneInputInput:focus-visible {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
  }

  .phone-input-error-text {
    color: #d32f2f;
    font-size: 0.75rem;
    margin: 3px 14px 0;
  }
`;

interface PhoneInputData {
  field_name?: string;
  label?: string;
  required?: boolean;
  defaultCountry?: string;
  value?: string;
}

interface PhoneInputProps {
  data: PhoneInputData;
  onChange?: (value: Record<string, string>) => void;
  mutable?: boolean;
  read_only?: boolean;
}

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (props, ref) => {
    const { field_name, required, defaultCountry, value: initialValue } =
      props.data || {};

    const [value, setValue] = useState<string | undefined>(initialValue);
    const [touched, setTouched] = useState(false);
    const hiddenInputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => hiddenInputRef.current as HTMLInputElement);

    const showError =
      touched && (required ? !value : Boolean(value) && !isValidPhoneNumber(value || ''));

    const handleChange = (next?: string) => {
      setValue(next);
      if (props.onChange && field_name) {
        props.onChange({ [field_name]: next || '' });
      }
    };

    return (
      <StyledWrapper className={showError ? 'has-error' : ''}>
        <PhoneInputBase
          international
          defaultCountry={(defaultCountry as any) || 'IN'}
          value={value}
          onChange={handleChange}
          onBlur={() => setTouched(true)}
          disabled={props.read_only}
        />
        {showError && (
          <div className="phone-input-error-text">
            {required && !value ? 'This field is required.' : 'Enter a valid phone number.'}
          </div>
        )}
        <input
          ref={hiddenInputRef}
          type="hidden"
          name={field_name}
          value={value || ''}
        />
      </StyledWrapper>
    );
  },
);

PhoneInput.displayName = 'PhoneInput';

export default PhoneInput;
